import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './projects.scss';
import './deletefeedback.scss';
import { PROJECTS_PROFILE } from '../../../Constants/strings';
import NoEmployeeDetails from '../EmployeeAchievementDetails/noEmployeeDetails';

const ProjectAccordion = ({
  project,
  index,
  expanded,
  handleChange,
  isPreviousProject,
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  // Access user profile and permissions from AuthContext
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Function to format the date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(
      new Date(dateString),
    );
  };

  // Function to format the total hours logged
  const formatTotalHoursLogged = (hours) => {
    return parseFloat(hours).toString();
  };

  // Function to get unique skills which are comma separated
  const getUniqueSkills = (skills) => {
    const splitSkills = skills.map(skill => skill.split(',')).flat();
    const uniqueSkills = [...new Set(splitSkills.map(skill => skill.trim()).filter(skill => skill))];
    return uniqueSkills;
  };

  const getFormattedDate = (date) => {
    const day = date.getDate().toString()
      .padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getDateFromAllocations = (allocations, findEarliest = true) => {
    if (!allocations || allocations.length === 0) return null;

    const targetDate = allocations.reduce((target, current) => {
      const currentDate = new Date(findEarliest ? current.startDate : current.endDate);
      if (findEarliest) {
        return currentDate < target ? currentDate : target;
      } else {
        return currentDate > target ? currentDate : target;
      }
    }, new Date(findEarliest ? allocations[0].startDate : allocations[0].endDate));

    return getFormattedDate(targetDate);
  };

  return (
    <Accordion
      className="project-details-accordion-sec"
      expanded={expanded === index + 1}
      onChange={handleChange(index + 1)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className="project-details-accordion-item"
      >
        <div>
          <Typography
            variant={'h6'}
            className={'project-details-accordion-heading'}
          >
            {project.projectName}
          </Typography>
        </div>
        {!isMobileView && (
          <div className="project-count-details">
            <Typography sx={{ color: 'text.secondary' }}>
              {isPreviousProject ? PROJECTS_PROFILE.START_DATE : PROJECTS_PROFILE.HOURS_LOGGED}{' '}
              <span>{isPreviousProject
                ? getDateFromAllocations(project.allocation, true)
                : formatTotalHoursLogged(project.totalHoursLogged)}</span>
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>|</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {isPreviousProject ? PROJECTS_PROFILE.END_DATE : PROJECTS_PROFILE.ALLOCATED}{' '}
              <span>{isPreviousProject
                ? getDateFromAllocations(project.allocation, false)
                : `${project.latestAllocation || 0}%`}</span>
            </Typography>
          </div>
        )}
      </AccordionSummary>
      <AccordionDetails className="project-details-accordion-details">
        <div className="project-details-accordion-panel">
          <div className="project-details-accordion-inner-sec">
            <div className="project-details1">
              {isMobileView && !isPreviousProject && (
                <div className="project-count-details">
                  <Typography sx={{ color: 'text.secondary' }}>
                    {PROJECTS_PROFILE.HOURS_LOGGED}{' '}
                    <span>
                      {formatTotalHoursLogged(project.totalHoursLogged)}
                    </span>
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>|</Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {PROJECTS_PROFILE.ALLOCATED}{' '}
                    <span>{project.latestAllocation || 0}%</span>
                  </Typography>
                </div>
              )}
              <div className={`skills-used ${isPreviousProject ? 'full-width-skills' : ''}`}>
                <h3>{PROJECTS_PROFILE.SKILLS_USED}</h3>
                <div className="projects-skill-mapped">
                  {!project?.skills
                  || project.skills.length === 0
                  || project.skills.every((skill) => !skill) ? (
                      <NoEmployeeDetails
                        message={PROJECTS_PROFILE.INFORMATION_NONE}
                        noEmployeeCss="no-recognition-css"
                      />
                    ) : (
                      getUniqueSkills(project.skills).map((skill) => (
                        <span key={skill}>{skill}</span>
                      ))
                    )}
                </div>
              </div>
              {!isPreviousProject && (
                <div className="allocation">
                  <h3>{PROJECTS_PROFILE.ALLOCATION}</h3>
                  <div className="projects-allocation-mapped">
                    {!project?.allocation
                  || project.allocation.length === 0
                  || project.allocation.every(
                    (allocation) =>
                      !allocation.startDate
                      || !allocation.endDate
                      || !allocation.billability,
                  ) ? (
                        <NoEmployeeDetails
                          message={PROJECTS_PROFILE.INFORMATION_NONE}
                          noEmployeeCss="no-recognition-css"
                        />
                      ) : (
                        project.allocation.map((allocation) => (
                          <div
                            className="projects-allocation-details"
                            key={allocation}
                          >
                            <span>
                              {formatDate(allocation.startDate)} -{' '}
                              {formatDate(allocation.endDate)}
                            </span>
                            <span>
                          - {PROJECTS_PROFILE.ALLOCATION}{' '}
                              <span className="allocation-percentage">
                                {allocation.billability}%
                              </span>
                            </span>
                          </div>
                        ))
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

ProjectAccordion.propTypes = {
  project: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expanded: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  handleChange: PropTypes.func.isRequired,
  isPreviousProject: PropTypes.bool,
};

export default ProjectAccordion;
